import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import ApiClient from "../../methods/api/apiClient";
import loader from "../../methods/loader";
import Layout from "../../components/global/layout";
import {
  RefreshCw,
  Clock,
  CheckCircle2,
  AlertCircle,
  Search,
  ListTodo,
  Layers,
  Sparkles,
  ArrowUpDown,
  Tag,
  Activity,
} from "lucide-react";

// ── Property Helpers ──
const getTypeLabel = (type) => {
  if (!type) return "—";
  const map = {
    CUSTOMER: "Customer",
    ITEM: "Product / Service",
    ESTIMATE: "Estimate",
    INVOICE: "Invoice",
  };
  return map[type.toUpperCase()] || type;
};

const getActionLabel = (action) => {
  if (!action) return "—";
  const map = {
    QUERY: "Fetch / Sync",
    CREATE: "Create",
    ADD: "Create",
  };
  return map[action.toUpperCase()] || action;
};

const getDescriptionLabel = (type, action) => {
  const t = (type || "").toUpperCase();
  const a = (action || "").toUpperCase();

  if (t === "CUSTOMER" && a === "QUERY") return "Sync Customers";
  if (t === "ITEM" && a === "QUERY") return "Sync Products & Services";
  if (t === "ESTIMATE" && a === "QUERY") return "Sync Estimates";
  if (t === "INVOICE" && a === "QUERY") return "Sync Invoices";
  if (t === "ESTIMATE" && a === "CREATE") return "Create Estimate Sync";
  if (t === "INVOICE" && a === "CREATE") return "Create Invoice Sync";
  if (t === "CUSTOMER" && (a === "CREATE" || a === "ADD")) return "Create Customer Sync";
  if (t === "ITEM" && (a === "CREATE" || a === "ADD")) return "Create Product Sync";

  const tName = getTypeLabel(type);
  const aName = getActionLabel(action);
  return `${tName} ${aName}`.trim();
};

const getJobDate = (job) => {
  if (job?.createdAt) return new Date(job.createdAt).toLocaleString();
  if (job?.updatedAt) return new Date(job.updatedAt).toLocaleString();
  if (job?._id && typeof job._id === "string" && job._id.length === 24) {
    try {
      const timestamp = parseInt(job._id.substring(0, 8), 16) * 1000;
      if (!isNaN(timestamp)) {
        return new Date(timestamp).toLocaleString();
      }
    } catch (e) {
      // fallback
    }
  }
  return "—";
};

const getStatusBadge = (status) => {
  const s = (status || "pending").toUpperCase();
  if (s === "COMPLETED" || s === "SUCCESS" || s === "DONE") {
    return (
      <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200 shadow-2xs">
        <CheckCircle2 className="size-3.5 text-emerald-600" />
        Completed
      </span>
    );
  }
  if (s === "PROCESSING" || s === "RUNNING") {
    return (
      <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-blue-50 text-blue-700 border border-blue-200 shadow-2xs">
        <RefreshCw className="size-3.5 animate-spin text-blue-600" />
        Processing
      </span>
    );
  }
  if (s === "FAILED" || s === "ERROR") {
    return (
      <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-red-50 text-red-700 border border-red-200 shadow-2xs">
        <AlertCircle className="size-3.5 text-red-600" />
        Failed
      </span>
    );
  }
  // Default PENDING
  return (
    <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-amber-50 text-amber-700 border border-amber-200 shadow-2xs">
      <span className="size-2 rounded-full bg-amber-500 animate-pulse" />
      Pending Sync
    </span>
  );
};

export default function QBDQueueStatus() {
  const user = useSelector((state) => state.user);
  const companyId = user?.id || user?._id;

  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [lastRefreshed, setLastRefreshed] = useState(null);

  const fetchJobs = async (showLoader = true) => {
    if (!companyId) return;
    if (showLoader) loader(true);
    setLoading(true);
    try {
      const res = await ApiClient.get(`qb-desktop/list-jobs?companyId=${companyId}`);
      if (res?.success) {
        setJobs(res?.data || []);
        setLastRefreshed(new Date());
      }
    } catch (err) {
      console.error("Failed to fetch queue jobs:", err);
    } finally {
      setLoading(false);
      if (showLoader) loader(false);
    }
  };

  useEffect(() => {
    fetchJobs(true);
  }, [companyId]);

  // Compute stat counts
  const totalCount = jobs.length;
  const pendingCount = jobs.filter(
    (j) => (j.status || "PENDING").toUpperCase() === "PENDING"
  ).length;
  const processingCount = jobs.filter(
    (j) => (j.status || "").toUpperCase() === "PROCESSING"
  ).length;
  const completedCount = jobs.filter((j) =>
    ["COMPLETED", "SUCCESS", "DONE"].includes((j.status || "").toUpperCase())
  ).length;
  const failedCount = jobs.filter((j) =>
    ["FAILED", "ERROR"].includes((j.status || "").toUpperCase())
  ).length;

  // Filter jobs by search and status tab
  const filteredJobs = jobs.filter((job) => {
    const status = (job.status || "PENDING").toUpperCase();
    if (statusFilter === "pending" && status !== "PENDING") return false;
    if (statusFilter === "processing" && status !== "PROCESSING") return false;
    if (
      statusFilter === "completed" &&
      !["COMPLETED", "SUCCESS", "DONE"].includes(status)
    )
      return false;
    if (statusFilter === "failed" && !["FAILED", "ERROR"].includes(status))
      return false;

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      const typeStr = (job.type || "").toLowerCase();
      const actionStr = (job.action || "").toLowerCase();
      const jobId = (job._id || job.id || "").toLowerCase();
      const descLabel = getDescriptionLabel(job.type, job.action).toLowerCase();

      return (
        typeStr.includes(q) ||
        actionStr.includes(q) ||
        jobId.includes(q) ||
        descLabel.includes(q)
      );
    }

    return true;
  });

  const STATUS_TABS = [
    { label: "All Jobs", value: "all", count: totalCount },
    { label: "Pending", value: "pending", count: pendingCount },
    { label: "Processing", value: "processing", count: processingCount },
    { label: "Completed", value: "completed", count: completedCount },
    { label: "Failed", value: "failed", count: failedCount },
  ];

  return (
    <Layout>
      <div className="max-xl:container xl:max-w-[1280px] mx-auto py-5 space-y-6">
        {/* Page Header */}
        <div className="bg-white shadow-lg rounded-2xl border border-gray-100 p-6 sm:p-7">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div>
              <div className="flex items-center gap-3">
                <div className="size-11 rounded-2xl bg-[#115B9D]/10 text-[#115B9D] flex items-center justify-center font-bold shadow-xs">
                  <ListTodo className="size-6" />
                </div>
                <div>
                  <h1 className="text-xl sm:text-2xl font-bold text-gray-900">
                    QuickBooks Desktop Queue Status
                  </h1>
                  <p className="text-xs sm:text-sm text-gray-500 mt-0.5">
                    Real-time list and sync progress of Web Connector queued jobs.
                  </p>
                </div>
              </div>
            </div>

            <div className="flex items-center gap-3">
              {lastRefreshed && (
                <span className="hidden sm:inline-flex items-center gap-1.5 text-xs text-gray-500">
                  <Clock className="size-3.5 text-gray-400" />
                  Updated: {lastRefreshed.toLocaleTimeString()}
                </span>
              )}

              <button
                type="button"
                onClick={() => fetchJobs(false)}
                disabled={loading}
                className="inline-flex items-center gap-2 bg-[#115B9D] hover:opacity-80 text-white text-xs font-semibold px-4 py-2.5 rounded-xl transition-all shadow-md shadow-[#115B9D]/20 disabled:opacity-60"
              >
                <RefreshCw className={`size-4 ${loading ? "animate-spin" : ""}`} />
                Refresh Queue
              </button>
            </div>
          </div>

          {/* Top Summary Stat Cards */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-6 pt-6 border-t border-gray-100">
            <div className="bg-gray-50/70 border border-gray-200/80 rounded-2xl p-4">
              <div className="flex items-center justify-between">
                <span className="text-xs font-semibold text-gray-500">Total Jobs</span>
                <Layers className="size-4 text-gray-400" />
              </div>
              <p className="text-2xl font-bold text-gray-900 mt-1">{totalCount}</p>
            </div>

            <div className="bg-amber-50/60 border border-amber-200/70 rounded-2xl p-4">
              <div className="flex items-center justify-between">
                <span className="text-xs font-semibold text-amber-700">Pending Sync</span>
                <Clock className="size-4 text-amber-500" />
              </div>
              <p className="text-2xl font-bold text-amber-900 mt-1">{pendingCount}</p>
            </div>

            <div className="bg-emerald-50/60 border border-emerald-200/70 rounded-2xl p-4">
              <div className="flex items-center justify-between">
                <span className="text-xs font-semibold text-emerald-700">Completed</span>
                <CheckCircle2 className="size-4 text-emerald-500" />
              </div>
              <p className="text-2xl font-bold text-emerald-900 mt-1">{completedCount}</p>
            </div>

            <div className="bg-red-50/60 border border-red-200/70 rounded-2xl p-4">
              <div className="flex items-center justify-between">
                <span className="text-xs font-semibold text-red-700">Failed / Error</span>
                <AlertCircle className="size-4 text-red-500" />
              </div>
              <p className="text-2xl font-bold text-red-900 mt-1">{failedCount}</p>
            </div>
          </div>
        </div>

        {/* Main Table & Filters */}
        <div className="bg-white shadow-lg rounded-2xl border border-gray-100 p-5 sm:p-7">
          {/* Controls: Tabs & Search */}
          <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
            {/* Status Filter Pills */}
            <div className="flex items-center gap-1.5 bg-gray-100/80 p-1.5 rounded-xl overflow-x-auto max-w-full">
              {STATUS_TABS.map((tab) => {
                const isActive = statusFilter === tab.value;
                return (
                  <button
                    key={tab.value}
                    type="button"
                    onClick={() => setStatusFilter(tab.value)}
                    className={`inline-flex items-center gap-2 px-3.5 py-1.5 rounded-lg text-xs font-semibold transition-all whitespace-nowrap ${isActive
                        ? "bg-white text-[#115B9D] shadow-xs"
                        : "text-gray-600 hover:text-gray-900 hover:bg-white/50"
                      }`}
                  >
                    {tab.label}
                    <span
                      className={`px-1.5 py-0.5 rounded-md text-[10px] font-bold ${isActive
                          ? "bg-[#115B9D]/10 text-[#115B9D]"
                          : "bg-gray-200 text-gray-600"
                        }`}
                    >
                      {tab.count}
                    </span>
                  </button>
                );
              })}
            </div>

            {/* Search Input */}
            <div className="relative w-full sm:w-64">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-gray-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search type, action, or ID..."
                className="w-full h-9 pl-9 pr-3 text-xs bg-gray-50 border border-gray-200 rounded-xl outline-none focus:ring-2 focus:ring-[#115B9D]/30 focus:border-[#115B9D] transition-all"
              />
            </div>
          </div>

          {/* Data Table */}
          {loading && jobs.length === 0 ? (
            <div className="flex justify-center items-center py-16">
              <div className="inline-flex items-center gap-2 text-sm text-gray-500">
                <RefreshCw className="size-5 animate-spin text-[#115B9D]" />
                Loading Queue Jobs...
              </div>
            </div>
          ) : filteredJobs.length === 0 ? (
            <div className="text-center py-16 border border-dashed border-gray-200 rounded-2xl bg-gray-50/50">
              <ListTodo className="size-10 text-gray-300 mx-auto mb-2" />
              <h3 className="text-base font-bold text-gray-800">No Queue Jobs Found</h3>
              <p className="text-xs text-gray-500 mt-1 max-w-md mx-auto">
                {searchQuery || statusFilter !== "all"
                  ? "No queue jobs matched your current filter or search criteria."
                  : "No sync jobs have been queued yet. Create an estimate, invoice, or trigger a queue sync in Settings."}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto border border-gray-200 rounded-2xl">
              <table className="w-full text-left border-collapse">
                <thead className="bg-gray-50 text-xs font-semibold text-gray-600 border-b border-gray-200">
                  <tr>
                    <th className="py-3.5 px-4 w-12 text-center">#</th>
                    <th className="py-3.5 px-4">Job ID</th>
                    <th className="py-3.5 px-4">Entity Type</th>
                    <th className="py-3.5 px-4">Action</th>
                    <th className="py-3.5 px-4">Description</th>
                    <th className="py-3.5 px-4">Status</th>
                    <th className="py-3.5 px-4">Created Date</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 text-xs text-gray-700">
                  {filteredJobs.map((job, idx) => {
                    const descLabel = getDescriptionLabel(job.type, job.action);
                    const dateStr = getJobDate(job);
                    const typeUpper = (job.type || "—").toUpperCase();
                    const actionUpper = (job.action || "—").toUpperCase();

                    return (
                      <tr key={job._id || job.id || idx} className="hover:bg-gray-50/60 transition-colors">
                        <td className="py-3.5 px-4 text-center font-medium text-gray-400">
                          {idx + 1}
                        </td>
                        <td className="py-3.5 px-4 font-mono text-[11px] font-semibold text-gray-800">
                          {job._id || job.id || "—"}
                        </td>
                        <td className="py-3.5 px-4">
                          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-bold bg-slate-100 text-slate-700 border border-slate-200">
                            <Tag className="size-3 text-slate-500" />
                            {typeUpper}
                          </span>
                        </td>
                        <td className="py-3.5 px-4">
                          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-bold bg-[#115B9D]/10 text-[#115B9D] border border-[#115B9D]/20">
                            <Activity className="size-3 text-[#115B9D]" />
                            {actionUpper}
                          </span>
                        </td>
                        <td className="py-3.5 px-4 font-semibold text-gray-900">
                          {descLabel}
                        </td>
                        <td className="py-3.5 px-4">{getStatusBadge(job.status)}</td>
                        <td className="py-3.5 px-4 text-gray-600 whitespace-nowrap">{dateStr}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {/* Pro Tip Footer */}
          <div className="mt-6 p-4 rounded-xl bg-blue-50/70 border border-blue-200/80 flex items-start gap-3 text-xs text-blue-900 leading-5">
            <Sparkles className="size-4 text-[#115B9D] mt-0.5 shrink-0" />
            <span>
              <strong>Web Connector Sync Tip:</strong> Queue jobs are fetched automatically whenever <strong>QuickBooks Web Connector (QBWC)</strong> runs on your computer. If Auto-Run is enabled in Web Connector, pending jobs process every 15 minutes automatically.
            </span>
          </div>
        </div>
      </div>
    </Layout>
  );
}
